import { useCallback, useEffect, useMemo, useState } from 'react';
import { Ban, Eye, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { cancelDirectCharge, createDirectCharge, listDirectCharges } from '../api/directCharges';
import { listCustomers } from '../api/customers';
import { getErrorMessage } from '../api/response';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import { LoadingState } from '../components/PageState';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { currency, dateTime } from '../utils/format';

const statusConfig = {
  pending: { label: 'Pendiente', color: 'amber' },
  paid: { label: 'Pagado', color: 'green' },
  cancelled: { label: 'Cancelado', color: 'red' },
  expired: { label: 'Expirado', color: 'slate' }
};

const emptyForm = {
  customer_id: '',
  amount: '',
  title: '',
  description: '',
  due_date: ''
};

const CobrosDirectos = () => {
  const [charges, setCharges] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const debouncedSearch = useDebouncedValue(search.trim());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [chargeData, customerData] = await Promise.all([
        listDirectCharges({
          status: status || undefined,
          search: debouncedSearch || undefined,
          customer_id: customerId || undefined,
          limit: 100
        }),
        listCustomers({ limit: 200 })
      ]);
      setCharges(chargeData);
      setCustomers(customerData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los cobros directos'));
    } finally {
      setLoading(false);
    }
  }, [customerId, debouncedSearch, status]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    return charges.reduce((acc, charge) => {
      acc.count += 1;
      acc.amount += Number(charge.amount || 0);
      if (charge.status === 'pending') {
        acc.pending += Number(charge.amount || 0);
      }
      return acc;
    }, { count: 0, amount: 0, pending: 0 });
  }, [charges]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFormOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      await createDirectCharge({
        customer_id: Number(form.customer_id),
        amount: Number(form.amount),
        title: form.title,
        description: form.description || null,
        due_date: form.due_date || null
      });
      toast.success('Cobro directo creado');
      resetForm();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo crear el cobro'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (charge) => {
    if (!window.confirm(`¿Cancelar el cobro "${charge.title}"?`)) {
      return;
    }

    try {
      await cancelDirectCharge(charge.id);
      toast.success('Cobro cancelado');
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cancelar el cobro'));
    }
  };

  const columns = [
    {
      key: 'title',
      label: 'Cobro',
      render: (row) => (
        <div>
          <p className="font-semibold text-gray-950">{row.title}</p>
          <p className="text-xs text-gray-500">{row.description || 'Sin descripción'}</p>
        </div>
      )
    },
    { key: 'customer_name', label: 'Cliente', render: (row) => row.customer_name || `#${row.customer_id}` },
    { key: 'amount', label: 'Monto', render: (row) => <span className="font-semibold text-gray-950">{currency(row.amount)}</span> },
    {
      key: 'status',
      label: 'Estado',
      render: (row) => {
        const config = statusConfig[row.status] || { label: row.status, color: 'slate' };
        return <StatusBadge label={config.label} color={config.color} />;
      }
    },
    { key: 'due_date', label: 'Vence', render: (row) => row.due_date || '-' },
    { key: 'created_at', label: 'Creado', render: (row) => dateTime(row.created_at) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedCharge(row)}
            className="rounded-md p-2 text-gray-700 hover:bg-gray-100"
            title="Ver detalle"
          >
            <Eye className="h-4 w-4" />
          </button>
          {row.status === 'pending' && (
            <button
              type="button"
              onClick={() => handleCancel(row)}
              className="rounded-md p-2 text-red-700 hover:bg-red-50"
              title="Cancelar cobro"
            >
              <Ban className="h-4 w-4" />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cobros Directos"
        description="Cobros manuales a clientes, separados de tienda, agenda y reservas."
        actions={<Button icon={Plus} onClick={() => setFormOpen(true)}>Nuevo cobro</Button>}
      />

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Cobros listados</p>
          <p className="mt-1 text-2xl font-semibold text-gray-950">{totals.count}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">Monto listado</p>
          <p className="mt-1 text-2xl font-semibold text-gray-950">{currency(totals.amount)}</p>
        </div>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">Pendiente por cobrar</p>
          <p className="mt-1 text-2xl font-semibold text-yellow-950">{currency(totals.pending)}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_180px_220px_auto] md:items-end">
          <FormField label="Buscar">
            <input
              className={inputClassName}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Cliente, motivo..."
            />
          </FormField>
          <FormField label="Estado">
            <select className={inputClassName} value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="paid">Pagado</option>
              <option value="cancelled">Cancelado</option>
              <option value="expired">Expirado</option>
            </select>
          </FormField>
          <FormField label="Cliente">
            <select className={inputClassName} value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Todos</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.full_name}</option>
              ))}
            </select>
          </FormField>
          <Button icon={Search} variant="secondary" onClick={loadData}>Filtrar</Button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : (
          <Table columns={columns} data={charges} emptyMessage="No hay cobros directos" />
        )}
      </section>

      <Modal
        open={formOpen}
        title="Nuevo cobro directo"
        onClose={resetForm}
        footer={(
          <>
            <Button variant="secondary" onClick={resetForm}>Cancelar</Button>
            <Button type="submit" form="direct-charge-form" disabled={saving}>
              {saving ? 'Guardando...' : 'Crear cobro'}
            </Button>
          </>
        )}
      >
        <form id="direct-charge-form" className="space-y-4" onSubmit={handleSubmit}>
          <FormField label="Cliente">
            <select className={inputClassName} required value={form.customer_id} onChange={(event) => updateForm('customer_id', event.target.value)}>
              <option value="">Selecciona cliente</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>{customer.full_name}</option>
              ))}
            </select>
          </FormField>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField label="Monto">
              <input className={inputClassName} required type="number" min="1" step="1" value={form.amount} onChange={(event) => updateForm('amount', event.target.value)} />
            </FormField>
            <FormField label="Fecha de vencimiento">
              <input className={inputClassName} type="date" value={form.due_date} onChange={(event) => updateForm('due_date', event.target.value)} />
            </FormField>
          </div>
          <FormField label="Título / motivo">
            <input className={inputClassName} required value={form.title} onChange={(event) => updateForm('title', event.target.value)} placeholder="Medicamento aplicado, diferencia de precio..." />
          </FormField>
          <FormField label="Descripción">
            <textarea className={inputClassName} rows={4} value={form.description} onChange={(event) => updateForm('description', event.target.value)} />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={Boolean(selectedCharge)}
        title="Detalle de cobro"
        onClose={() => setSelectedCharge(null)}
        footer={(
          <>
            {selectedCharge?.status === 'pending' && (
              <Button variant="danger" icon={Ban} onClick={() => handleCancel(selectedCharge)}>Cancelar cobro</Button>
            )}
            <Button variant="secondary" onClick={() => setSelectedCharge(null)}>Cerrar</Button>
          </>
        )}
      >
        {selectedCharge && (
          <div className="grid gap-3 text-sm md:grid-cols-2">
            <p><span className="font-semibold">Cliente:</span> {selectedCharge.customer_name}</p>
            <p><span className="font-semibold">Estado:</span> {selectedCharge.status}</p>
            <p><span className="font-semibold">Monto:</span> {currency(selectedCharge.amount)}</p>
            <p><span className="font-semibold">Vence:</span> {selectedCharge.due_date || '-'}</p>
            <p className="md:col-span-2"><span className="font-semibold">Motivo:</span> {selectedCharge.title}</p>
            <p className="md:col-span-2"><span className="font-semibold">Descripción:</span> {selectedCharge.description || '-'}</p>
            <p><span className="font-semibold">Pago:</span> {selectedCharge.payment_id || '-'}</p>
            <p><span className="font-semibold">Creado:</span> {dateTime(selectedCharge.created_at)}</p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CobrosDirectos;
